import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SpuControllerService } from '@app/api/goods-service/api/spuController.service';
import { GoodsSkuVO } from '@app/api/goods-service/model/goodsSkuVO';
import { VendorSkuBulkCreateDTO } from '@app/api/vendor-service/model/vendorSkuBulkCreateDTO';
import { validateEmail } from '@shared/utils/email-validator.util';
import { validatePhoneNumber } from '@shared/utils/phone-validator.util';
import { VendorService } from './vendor.service';

type CsvRow = Record<string, string>;

interface ParsedCsv {
  headers: string[];
  rows: CsvRow[];
}

interface VendorSeedRow {
  vendorCode: string;
  vendorName: string;
  taxNumber?: string;
  vendorEmail: string;
  vendorPhone: string;
  vendorAddress: string;
  country?: string;
  active: boolean;
}

export interface VendorSeedImportResult {
  rowCount: number;
  skippedCount: number;
}

export interface VendorSkuXrefImportResult {
  createdCount: number;
  skippedCount: number;
}

/** Thrown when CSV header/columns do not match the expected import template. */
export class VendorImportFormatError extends Error {
  readonly missingColumns: string[];

  constructor(missingColumns: string[]) {
    super('INVALID_CSV_FORMAT');
    this.name = 'VendorImportFormatError';
    this.missingColumns = missingColumns;
  }
}

@Injectable({ providedIn: 'root' })
export class VendorImportService {
  private readonly vendorService = inject(VendorService);
  private readonly spuController = inject(SpuControllerService);

  private static readonly VENDOR_SEED_REQUIRED_COLUMNS = [
    'vendor_code',
    'vendor_name',
    'vendor_email',
    'vendor_phone',
    'vendor_address',
  ] as const;

  private static readonly FM07_XREF_REQUIRED_COLUMNS = [
    'map_status',
    'new_vendor_code',
    'new_sku_code',
  ] as const;

  async importVendorSeed(file: File): Promise<VendorSeedImportResult> {
    const text = await file.text();
    const { headers, rows } = this.parseCsv(text);
    this.assertRequiredColumns(
      headers,
      VendorImportService.VENDOR_SEED_REQUIRED_COLUMNS,
    );

    const candidates = rows
      .filter((r) => r['vendor_code'])
      .map(
        (r): VendorSeedRow => ({
          vendorCode: r['vendor_code']?.trim(),
          vendorName: r['vendor_name']?.trim(),
          taxNumber: this.normalizeNullable(r['tax_number']),
          vendorEmail: r['vendor_email']?.trim(),
          // Excel/CSV export đôi khi làm mất "0" đầu số điện thoại VN (vd 0708788284 -> 708788284).
          // Normalize lại để validate/filter khớp với format hệ thống đang kỳ vọng.
          vendorPhone: this.normalizeVietnamPhoneFromCsv(r['vendor_phone']),
          vendorAddress: r['vendor_address']?.trim(),
          country: this.normalizeNullable(r['country']),
          active: this.parseBoolean(r['active']),
        }),
      );

    const vendors = candidates.filter(
      (v) =>
        !!v.vendorCode &&
        !!v.vendorName &&
        !!v.vendorEmail &&
        !!v.vendorPhone &&
        !!v.vendorAddress &&
        v.country === 'VN' &&
        validateEmail(v.vendorEmail).isValid &&
        validatePhoneNumber(v.vendorPhone, 'VN').isValid,
    );
    const skippedCount = Math.max(0, candidates.length - vendors.length);

    if (vendors.length > 0) {
      await firstValueFrom(
        this.vendorService.createVendorsBulk(
          vendors.map((v) => ({
            vendorCode: v.vendorCode,
            vendorName: v.vendorName,
            taxNumber: v.taxNumber,
            vendorEmail: v.vendorEmail,
            vendorPhone: v.vendorPhone,
            vendorAddress: v.vendorAddress,
            country: v.country,
            status: v.active ? 'ACTIVE' : 'DRAFT',
          })) as any,
        ),
      );
    }

    return { rowCount: vendors.length, skippedCount };
  }

  async importFm07Xref(file: File): Promise<VendorSkuXrefImportResult> {
    const text = await file.text();
    const { headers, rows } = this.parseCsv(text);
    this.assertRequiredColumns(
      headers,
      VendorImportService.FM07_XREF_REQUIRED_COLUMNS,
    );

    const mappedRows = rows.filter(
      (r) => this.unquoteCsvCell(r['map_status']).toUpperCase() === 'MAPPED',
    );

    const candidates = mappedRows
      .map((r) => ({
        vendorCode: this.unquoteCsvCell(r['new_vendor_code']),
        vendorSkuCode: this.unquoteCsvCell(r['new_sku_code']),
        skuCode: this.unquoteCsvCell(r['new_sku_code']),
      }))
      .filter((c) => c.vendorCode && c.skuCode);

    const uniqueVendorCodes = Array.from(
      new Set(candidates.map((c) => c.vendorCode)),
    );
    const activeVendorCodes = await this.resolveActiveVendorCodes(
      uniqueVendorCodes,
    );
    const eligibleCandidates = candidates.filter((c) =>
      activeVendorCodes.has(c.vendorCode),
    );
    const skippedInactiveVendors = candidates.length - eligibleCandidates.length;

    const uniqueSkuCodes: string[] = Array.from(
      new Set(eligibleCandidates.map((c) => c.skuCode)),
    );
    if (uniqueSkuCodes.length === 0) {
      return {
        createdCount: 0,
        skippedCount: mappedRows.length,
      };
    }

    // Force JSON: generated client Accept is "*/*", so HttpClient otherwise
    // returns a Blob and SKU mapping becomes empty (skip bulk).
    const skuInfos = await this.asSkuList(
      await firstValueFrom(
        this.spuController.getSkuListByCodes(uniqueSkuCodes, 'body', false, {
          httpHeaderAccept: 'application/json' as any,
        }),
      ),
    );

    const skuNameByCode = new Map<string, string>();
    for (const s of skuInfos) {
      const anySku = s as any;
      const code = this.normalizeSkuCode(anySku.skuCode ?? anySku.sku_code);
      const name = String(anySku.skuName ?? anySku.sku_name ?? '').trim();
      if (code && name) {
        skuNameByCode.set(code, name);
      }
    }

    const payload: VendorSkuBulkCreateDTO[] = [];
    for (const c of eligibleCandidates) {
      const skuName = skuNameByCode.get(this.normalizeSkuCode(c.skuCode));
      if (!skuName) {
        continue;
      }
      payload.push({
        vendorCode: c.vendorCode,
        vendorSkuCode: c.vendorSkuCode,
        vendorProductName: skuName,
        skuCode: c.skuCode,
        skuName,
        active: true,
      });
    }

    if (payload.length === 0) {
      return {
        createdCount: 0,
        skippedCount: candidates.length,
      };
    }

    const result = await firstValueFrom(
      this.vendorService.createVendorSkuBulk(payload),
    );

    return {
      createdCount: result?.createdCount ?? 0,
      skippedCount:
        (result?.skippedCount ?? 0) +
        skippedInactiveVendors +
        Math.max(0, eligibleCandidates.length - payload.length),
    };
  }

  /**
   * Load ACTIVE vendors from list API, then keep only CSV vendor codes that exist there.
   * Uses GET /v1/vendor?status=ACTIVE&deleteFlag=false&page=&size=...
   */
  private async resolveActiveVendorCodes(
    vendorCodes: string[],
  ): Promise<Set<string>> {
    const wanted = new Set(vendorCodes.filter((c) => !!c));
    const active = new Set<string>();
    if (wanted.size === 0) {
      return active;
    }

    const pageSize = 100;
    let page = 0;
    let totalRecords = Number.POSITIVE_INFINITY;

    while (active.size < wanted.size && page * pageSize < totalRecords) {
      const response = await firstValueFrom(
        this.vendorService.getVendors({
          status: 'ACTIVE',
          page,
          size: pageSize,
          sortBy: 'creationDate',
          ascending: false,
        }),
      );

      totalRecords = response.totalRecords ?? 0;
      const rows = response.data ?? [];
      if (rows.length === 0) {
        break;
      }

      for (const vendor of rows) {
        const code = vendor.code?.trim();
        if (code && wanted.has(code)) {
          active.add(code);
        }
      }

      page += 1;
      // Safety guard against infinite loop on bad pagination metadata
      if (page > 100) {
        break;
      }
    }

    return active;
  }

  private assertRequiredColumns(
    headers: string[],
    requiredColumns: readonly string[],
  ): void {
    const headerSet = new Set(headers);
    const missingColumns = requiredColumns.filter((c) => !headerSet.has(c));
    if (missingColumns.length > 0 || headers.length === 0) {
      throw new VendorImportFormatError(
        missingColumns.length > 0
          ? [...missingColumns]
          : [...requiredColumns],
      );
    }
  }

  /** Unwrap SKU list from array, wrapped `{ data }`, or Blob. */
  private async asSkuList(payload: unknown): Promise<GoodsSkuVO[]> {
    if (typeof Blob !== 'undefined' && payload instanceof Blob) {
      try {
        return this.asSkuListSync(JSON.parse(await payload.text()));
      } catch {
        return [];
      }
    }
    return this.asSkuListSync(payload);
  }

  private asSkuListSync(payload: unknown): GoodsSkuVO[] {
    if (Array.isArray(payload)) {
      return payload as GoodsSkuVO[];
    }
    if (typeof payload === 'string') {
      try {
        return this.asSkuListSync(JSON.parse(payload));
      } catch {
        return [];
      }
    }
    if (payload && typeof payload === 'object') {
      const wrapped = payload as Record<string, unknown>;
      const nested =
        wrapped['data'] ??
        wrapped['content'] ??
        wrapped['pageContent'] ??
        wrapped['body'];
      if (Array.isArray(nested)) {
        return nested as GoodsSkuVO[];
      }
      if (wrapped['skuCode'] || wrapped['sku_code']) {
        return [payload as GoodsSkuVO];
      }
    }
    return [];
  }

  private unquoteCsvCell(value: string | undefined | null): string {
    return String(value ?? '')
      .trim()
      .replace(/^["']+|["']+$/g, '')
      .trim();
  }

  private normalizeSkuCode(value: string | undefined | null): string {
    return this.unquoteCsvCell(value).toUpperCase();
  }

  private normalizeNullable(v: string | undefined | null): string | undefined {
    const t = (v ?? '').trim();
    return t ? t : undefined;
  }

  private normalizeVietnamPhoneFromCsv(phone: string | undefined | null): string {
    const t = (phone ?? '').trim();
    if (!t) return '';

    // Keep "+84" or "0" prefix if present, otherwise try to detect VN phone without prefix.
    const cleaned = t.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+84') || cleaned.startsWith('0')) {
      return cleaned;
    }

    // If CSV is missing the leading "0" but still looks like a valid VN phone:
    // - Mobile numbers: 0[35789]\d{8} -> without leading 0: [35789]\d{8}
    // - Landline numbers (VN pattern in validator): 0[2]\d{9} -> without leading 0: 2\d{9}
    if (/^[35789]\d{8}$/.test(cleaned) || /^2\d{9}$/.test(cleaned)) {
      return `0${cleaned}`;
    }

    return cleaned;
  }

  private parseBoolean(v: string | undefined): boolean {
    const t = (v ?? '').trim().toUpperCase();
    return t === 'TRUE' || t === '1';
  }

  private parseCsv(text: string): ParsedCsv {
    const trimmed = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').trim();
    if (!trimmed) {
      return { headers: [], rows: [] };
    }

    const lines = trimmed
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const first = lines[0] ?? '';
    const delimiter = first.includes('\t')
      ? '\t'
      : first.includes(';') && !first.includes(',')
        ? ';'
        : ',';
    const headers = lines[0]
      .split(delimiter)
      .map((h) => this.unquoteCsvCell(h.replace(/^\uFEFF/, '')).toLowerCase())
      .filter((h) => h.length > 0);
    const rows: CsvRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(delimiter);
      if (parts.every((p) => (p ?? '').trim() === '')) {
        continue;
      }

      const row: CsvRow = {};
      for (let c = 0; c < headers.length; c++) {
        row[headers[c]] = this.unquoteCsvCell(parts[c]);
      }
      rows.push(row);
    }

    return { headers, rows };
  }
}
